'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface SearchResult {
  id: string;
  type: 'device' | 'ip' | 'subnet' | 'vlan';
  title: string;
  subtitle: string;
  href: string;
}

const typeConfig: Record<string, { icon: string; color: string; label: string }> = {
  device: { icon: 'ServerIcon', color: 'text-sky-400', label: 'Dispositivo' },
  ip: { icon: 'GlobeAltIcon', color: 'text-emerald-400', label: 'IP' },
  subnet: { icon: 'CircleStackIcon', color: 'text-purple-400', label: 'Subnet' },
  vlan: { icon: 'ShareIcon', color: 'text-amber-400', label: 'VLAN' },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const supabase = createClient();

  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const [
        { data: devices },
        { data: ips },
        { data: subnets },
        { data: vlans },
      ] = await Promise.all([
        supabase.from('devices').select('id, hostname, ip_address, categoria, reparto').or(`hostname.ilike.%${q}%,ip_address.ilike.%${q}%,reparto.ilike.%${q}%,modello.ilike.%${q}%`).limit(5),
        supabase.from('ip_addresses').select('id, ip, hostname, stato').or(`ip.ilike.%${q}%,hostname.ilike.%${q}%`).limit(4),
        supabase.from('subnets').select('id, nome, cidr, area').or(`nome.ilike.%${q}%,cidr.ilike.%${q}%`).limit(3),
        supabase.from('vlans').select('id, nome, vlan_id, area').or(`nome.ilike.%${q}%`).limit(3),
      ]);

      const r: SearchResult[] = [
        ...(devices || []).map(d => ({
          id: d.id,
          type: 'device' as const,
          title: d.hostname,
          subtitle: `${d.categoria} · ${d.ip_address || 'No IP'} · ${d.reparto || ''}`,
          href: '/device-inventory',
        })),
        ...(ips || []).map(i => ({
          id: i.id,
          type: 'ip' as const,
          title: i.ip,
          subtitle: `${i.hostname || 'Nessun hostname'} · ${i.stato}`,
          href: '/ip-address-management',
        })),
        ...(subnets || []).map(s => ({
          id: s.id,
          type: 'subnet' as const,
          title: s.nome,
          subtitle: `${s.cidr} · ${s.area}`,
          href: '/subnet-vlan',
        })),
        ...(vlans || []).map(v => ({
          id: v.id,
          type: 'vlan' as const,
          title: v.nome,
          subtitle: `VLAN ${v.vlan_id} · ${v.area}`,
          href: '/subnet-vlan',
        })),
      ];
      setResults(r);
      setActiveIdx(-1);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
  }, [query, search]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && activeIdx >= 0) {
      router.push(results[activeIdx].href);
      setOpen(false);
      setQuery('');
    }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const handleSelect = (r: SearchResult) => {
    router.push(r.href);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <div className="relative">
        <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Cerca dispositivi, IP, subnet..."
          className="w-full bg-surface-elevated border border-border rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors"
        />
        {loading && (
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin w-4 h-4 text-sky-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
      </div>

      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-surface border border-border rounded-xl shadow-2xl z-50 overflow-hidden max-h-80 overflow-y-auto scrollbar-thin">
          {results.length === 0 && !loading ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-muted-color">Nessun risultato per "{query}"</p>
            </div>
          ) : (
            <ul>
              {results.map((r, idx) => {
                const tc = typeConfig[r.type];
                return (
                  <li key={`${r.type}-${r.id}`}>
                    <button
                      onClick={() => handleSelect(r)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${idx === activeIdx ? 'bg-sky-500/10' : 'hover:bg-surface-elevated'}`}
                    >
                      <div className={`w-7 h-7 rounded-lg bg-surface-elevated flex items-center justify-center flex-shrink-0`}>
                        <Icon name={tc.icon as any} size={14} className={tc.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{r.title}</p>
                        <p className="text-xs text-muted-color truncate">{r.subtitle}</p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-surface-elevated ${tc.color}`}>
                        {tc.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
